/**
 * AuthLayout — shell used by /auth (passwordless entry) and the
 * standalone token-gated onboarding routes.
 *
 * Layout goals:
 *   - Restrained background with an ambient tint — theme aware via
 *     data-theme.
 *   - GreenCharge wordmark anchors the top of the auth column so
 *     every step (email → OTP → done) reads as an official
 *     GreenCharge screen. Deliberately understated in size so the
 *     form remains the focal point.
 *   - Big heading, generous line-height, roomy field spacing.
 *   - Vertically centered ~440px column; on mobile the form flows
 *     naturally so the keyboard never pushes the primary CTA off-screen.
 *   - Compact theme toggle top-right, deliberately quiet.
 *
 * Optional props:
 *   - `back`: `{ onClick, label }` — renders a step-back affordance
 *     above the heading, used by multi-step flows to reset to a
 *     previous local state (e.g. "Use a different email address").
 *     Distinct from the top-left marketing-back button below.
 *   - `showMarketingBack`: boolean (default true) — controls the
 *     upper-left "Back" button that returns the user to the
 *     greencharge-main marketing site. Screens that should NOT expose
 *     that exit (e.g. token-gated onboarding that has already burnt a
 *     step) can opt out with `showMarketingBack={false}`.
 */

import React from 'react';
import { LuChevronLeft } from 'react-icons/lu';
import ThemeToggle from '../../theme/ThemeToggle.jsx';
import GreenChargeLogo from '../Common/GreenChargeLogo.jsx';
import useMarketingBack from '../../hooks/useMarketingBack.js';
import './AuthLayout.less';

function BackButton({ back }) {
  if (!back || typeof back.onClick !== 'function') return null;
  return (
    <button
      type="button"
      className="auth-back"
      onClick={back.onClick}
      aria-label={back.label || 'Back'}
    >
      <span aria-hidden="true">←</span>
      <span>{back.label || 'Back'}</span>
    </button>
  );
}

/**
 * Top-left "Back" affordance that lets a user exit the auth flow and
 * return to the marketing site. Rendered above the logo so the entry
 * point is discoverable no matter which auth step the user is on.
 *
 * The navigation decision lives in `useMarketingBack` so the same
 * behaviour can be reused outside AuthLayout if a future flow needs it
 * without duplicating the history-vs-external-URL logic.
 */
function MarketingBackButton() {
  const { handleBack } = useMarketingBack();
  return (
    <button
      type="button"
      className="auth-marketing-back"
      onClick={handleBack}
      aria-label="Go back"
    >
      <LuChevronLeft className="auth-marketing-back-icon" aria-hidden="true" />
      <span>Back</span>
    </button>
  );
}

function AuthLayout({ title, subtitle, children, footer, back, showMarketingBack = true }) {
  return (
    <div className="auth-shell">
      <div className="auth-shell-ambient" aria-hidden="true" />

      {/* Compact theme toggle anchored top-right — deliberately quiet
          so the auth form remains the focal point. */}
      <header className="auth-theme-toggle-wrap">
        <ThemeToggle />
      </header>

      <main className="auth-shell-main">
        <div className="auth-content" role="region" aria-label={title}>
          {/* Marketing-site back — top-left, above the brand wordmark.
              Kept visually distinct from the local step-back below so
              the two never read as the same control. Opted out only
              when a screen should not expose the exit. */}
          {showMarketingBack && <MarketingBackButton />}
          {/* Brand wordmark — sits above the heading and step-back
              link, aligned with the same left edge as the form fields
              (both inherit .auth-content's max-width). The shared
              GreenChargeLogo component picks the light/dark
              variant automatically from the resolved theme. */}
          <GreenChargeLogo
            className="auth-brand-logo"
            height={40}
            style={{ maxWidth: 180 }}
          />
          <BackButton back={back} />
          {title && <h1 className="auth-title">{title}</h1>}
          {subtitle && <p className="auth-subtitle">{subtitle}</p>}
          <div className="auth-body">{children}</div>
          {footer && <div className="auth-footer">{footer}</div>}
        </div>
      </main>
    </div>
  );
}

export default AuthLayout;
